import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import useLoaderStore from './useLoaderStore';
import { useToastStore } from './toastStore';
// @ts-ignore
import * as utils from "../utils/utils.js";
import { useAuthStore } from './authStore.js';
import { fetchWithHeaders } from '../api/fetchWithHeaders.js';

export interface Negotiation {
  negotiation_id?: number;
  index_id: number;
  name: string;
  description?: string | null;
  comments?: string | null;
  status?: string;
  is_deleted?: boolean;
  // Original Dashboard fields (will be blank initially)
  contact?: string;
  article?: string;
  bundle?: string;
  countries?: string;
  supplier?: string;
  bundle_value?: number;
}

export interface NegotiationForm {
  brand: "YES" | "NO";
  extra_goals: string;
  target_savings: string;
  target_simulated_savings: string;
}

export interface Tender {
  tender_id: number;
  title: string;
  owner?: string;
  commodity?: string;
  region?: string;
  department?: string;
  currency?: string;
  sourcing_project_id?: number;
  negotiation_id?: number;
  display?: boolean;
}

export interface TenderBid {
  bid_id: number;
  tender_id: number;
  supplier_id: number;
  file_name: string | null;
  supplier_code: string;
  supplier_name: string;
  submitted_at: string | null;
  valid_until: string | null;
  response_comment: string | null;
  is_encumbant: boolean | null;
  product_id: number;
  product_variant_id: number | null;
  quantity_offered: number | null;
  min_order_quantity: number | null;
  unit_price: number | null;
  price: number | null;
  quantity: number;
  unit_of_measure: string;
  extended_price: number;
  currency: string;
  package_cost: number | null;
  logistic_cost: number | null;
  exw_price: number | null;
  total_price: number | null;
  total_ddp_price: number | null;
  delivery_location: string | null;
  payment_term: string | null;
  rebate_offer: string | null;
  volume_discount: string | null;
  offered_incoterm: string | null;
  product_description: string;
  article_description: string;
  country_of_origin: string;
  material_number: string;
  material_entity_level: string | null;
  buyer_legacy_material_id: string;
  material_type: string;
  product_specification_via_app: string;
  production_facility: string;
  plm_time_period: string;
  material_group: string;
  item_category: string;
  type_field: string;
}

// Sourcing product interfaces (using tender_sourcing_product)
export interface SourcingProductOption {
  product_description: string;
  variant_id: string | null;
  display: string | null;
  display_description: string | null;
}

export interface ProductInfo {
  product_description: string;
  variant_id: string | null;
  display: string | null;
  display_description: string | null;
}

export interface ProductBidOverviewCard {
  supplier_id: number;
  supplier_name: string | null;
  current_bid: number | null;
  current_currency: string | null;
  cogs: number | null;
  target_price: number | null;
  price_gap: number | null;
  price_gap_text: string | null;
  product_quality: string | null;
  incoterm: string | null;
  sourcing_country: string | null;
  variant_count: number | null;
}

export interface SpecificationGapItem {
  supplier_id: number;
  supplier_name: string | null;
  compliance_status: string;
  key_issues: string[];
}

export interface ProductBidScreenResponse {
  product: ProductInfo | null;
  bid_overview: ProductBidOverviewCard[];
  specification_gaps: SpecificationGapItem[];
}

interface NegotiationsState {
  negotiations: Negotiation[];
  loading: boolean;
  error: string | null;
  data: NegotiationForm;
  // Analytics
  avgPriceBySupplier: { supplier_id: number; supplier_name: string; avg_price: number | null }[];
  // Tender bids
  tenderBids: TenderBid[];
  allProductDescriptions: string[];
  // Tenders
  tenders: Tender[];
  selectedTender: Tender | null;
  tendersLoading: boolean;
  productLists: any[];
  tenderSummary: any[];
  // Sourcing product state (using tender_sourcing_product)
  sourcingProducts: SourcingProductOption[];
  countryLists: string[];
  productBidSummary: ProductBidScreenResponse | null;
  productBidSummaryLoading: boolean;
  // CRUD operations for /api/v1/negotiations
  fetchNegotiations: () => Promise<void>;
  fetchNegotiationById: (negotiation_id: number) => Promise<Negotiation | null>;
  createNegotiation: (name: string, description?: string) => Promise<Negotiation | null>;
  updateNegotiation: (negotiation_id: number, data: Partial<Negotiation>) => Promise<Negotiation | null>;
  deleteNegotiation: (negotiation_id: number) => Promise<boolean>;

  // Analytics operations
  fetchAvgPriceBySupplier: (negotiation_id: number, product_descriptions?: string[]) => Promise<void>;

  // Tender operations
  fetchTenderBids: (negotiation_id: number, product_descriptions?: string[]) => Promise<void>;
  fetchTenders: (negotiation_id: number) => Promise<void>;
  setSelectedTender: (tender: Tender | null) => void;
  updateSupplierBid: (
    negotiation_id: number,
    product_description: string,
    supplier_id: number | string,
    currentBid: number
  ) => Promise<any | null>;

  // Local state management
  setNegotiations: (negotiations: Negotiation[]) => void;
  addNegotiation: (negotiation: Negotiation) => void;
  removeNegotiation: (negotiation_id: number) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  // Form data management
  setBrand: (brand: "YES" | "NO") => void;
  setExtraGoals: (extra_goals: string) => void;
  setTargetSavings: (target_savings: string) => void;
  setTargetSimulatedSavings: (target_simulated_savings: string) => void;
  updateConfig: () => void;
  resetInitialData: () => void;
  resetAllProductDescriptions: () => void;
  fetchProductLists: (negotiation_id: number) => Promise<void>;
  fetchTenderSummary: (negotiation_id: number, product_descriptions: string) => Promise<void>;
  clearProducts: () => void;
  // Sourcing product operations (using tender_sourcing_product)
  fetchSourcingProducts: (negotiation_id: number) => Promise<void>;
  fetchCountryLists: (negotiation_id: number, variant_id: string) => Promise<void>;
  fetchProductBidSummary: (negotiation_id: number, variant_id: string, sourcing_country?: string) => Promise<void>;
  clearSourcingProductData: () => void;
}

const initialData: NegotiationForm = {
  brand: "NO",
  extra_goals: '',
  target_savings: "",
  target_simulated_savings: "",
};

export const useNegotiationsStore = create<NegotiationsState>()(
  devtools(
    // persist(   <= to make it persistent uncomment it
       (set, get) => ({
      negotiations: [],
      loading: false,
      error: null,
      data: initialData,
      avgPriceBySupplier: [],
      tenderBids: [],
      allProductDescriptions: [],
      tenders: [],
      selectedTender: null,
      tendersLoading: false,
      productLists: [],
      tenderSummary: [],
      // Sourcing product state
      sourcingProducts: [],
      countryLists: [],
      productBidSummary: null,
      productBidSummaryLoading: false,

      // CRUD operations for /api/v1/negotiations
      fetchNegotiations: async () => {
        const { showLoader } = useLoaderStore.getState();
        const url = import.meta.env.VITE_API_BASE_URL;
        const { error } = useToastStore.getState();
        set({ loading: true, error: null });
        showLoader(true);
        
        try {
          const response = await fetchWithHeaders(`${url}api/v1/negotiation-packages/`, {
            method: 'GET',
          });
          if (!response.ok) {
            error("Error", `Failed to fetch negotiations: ${response.statusText}`);
          }
          
          const val = await response.json();
          const negotiations = val.map((negotiation: Negotiation) => {
            return {
              ...negotiation,
              bundle_value_updated: utils.formatCost(negotiation.bundle_value || 0),
            };
          })
          set({ negotiations, loading: false });
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to fetch negotiations';
          error("Error", `Failed to fetch negotiations: ${errorMessage}`);
          set({ error: errorMessage, loading: false });
          console.error('Error fetching negotiations:', error);
        } finally {
          showLoader(false);
        }
      },

      // Analytics operations
      fetchAvgPriceBySupplier: async (negotiation_id: number, product_descriptions?: string[]) => {
        const { showLoader } = useLoaderStore.getState();
        const url = import.meta.env.VITE_API_BASE_URL;
        const { error } = useToastStore.getState();

        if (!negotiation_id) return;
        set({ loading: true, error: null });
        showLoader(true);
        try {
          let apiUrl = `${url}api/v1/negotiation-packages/${negotiation_id}/analytics/avg-price-by-supplier`;
          if (product_descriptions && product_descriptions.length > 0) {
            const commaSeparatedProducts = product_descriptions.join(',');
            apiUrl += `?product_description=${encodeURIComponent(commaSeparatedProducts)}`;
          }
          
          const response = await fetchWithHeaders(apiUrl, {
            method: 'GET',
          });
          if (!response.ok) {
            error("Error", `Failed to fetch avg price by supplier: ${response.statusText}`);
          }
          
          const data = await response.json();
          let updatedData= [];
          if(data.length > 0) {
            updatedData = data.map((item: any) => {
              return {
               ...item,
                avg_price: utils.roundToTwo(item.avg_price),
              }
            })
          }

          set({ avgPriceBySupplier: updatedData, loading: false });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to fetch analytics';
          set({ error: errorMessage, loading: false });
          console.error('Error fetching avg price by supplier:', error);
        } finally {
          showLoader(false);
        }
      },

      // Tender operations
      fetchTenderBids: async (negotiation_id: number, product_descriptions?: string[]) => {
        const { showLoader } = useLoaderStore.getState();
        const url = import.meta.env.VITE_API_BASE_URL;
        const { error } = useToastStore.getState();

        if (!negotiation_id) return;
        set({ loading: true, error: null });
        showLoader(true);
        
        try {
          let apiUrl = `${url}api/v1/tenders/bids?negotiation_id=${negotiation_id}`;
          if (product_descriptions && product_descriptions.length > 0) {
            const commaSeparatedProducts = product_descriptions.join(',');
            apiUrl += `&product_description=${encodeURIComponent(commaSeparatedProducts)}`;
          }
          
          const response = await fetchWithHeaders(apiUrl, {
            method: 'GET',
          });
          
          if (!response.ok) {
            error("Error", `Failed to fetch tender bids: ${response.statusText}`);
          }
          
          const data = await response.json();
          // Only update allProductDescriptions on initial load (when no filter)
          const currentDescriptions = get().allProductDescriptions;
          if (currentDescriptions.length === 0) {
            const productDescriptions = [...new Set(data.map((bid: TenderBid) => bid.product_description).filter(Boolean))] as string[];
            set({ tenderBids: data, allProductDescriptions: productDescriptions, loading: false });
          } else {
            set({ tenderBids: data, loading: false });
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to fetch tender bids';
          set({ error: errorMessage, loading: false });
          console.error('Error fetching tender bids:', error);
        } finally {
          showLoader(false);
        }
      },

      // Fetch tenders for a negotiation
      fetchTenders: async (negotiation_id: number) => {
        const url = import.meta.env.VITE_API_BASE_URL;
        const { error } = useToastStore.getState();
        
        set({ tendersLoading: true });
        
        try {
          const response = await fetchWithHeaders(`${url}api/v1/tenders/?negotiation_id=${negotiation_id}`, {
            method: 'GET',
          });
          
          if (!response.ok) {
            error("Error", `Failed to fetch tenders: ${response.statusText}`);
          }
          
          const data = await response.json();
          set({ tenders: data, tendersLoading: false });
          
          // If we have tenders and no tender is selected, auto-select the first one
          if (data.length > 0 && !get().selectedTender) {
            set({ selectedTender: data[0] });
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to fetch tenders';
          set({ error: errorMessage, tendersLoading: false });
          console.error('Error fetching tenders:', error);
        }
      },

      setSelectedTender: (tender: Tender | null) => {
        set({ selectedTender: tender });
      },

      fetchNegotiationById: async (negotiation_id: number) => {
        const { showLoader } = useLoaderStore.getState();
        const url = import.meta.env.VITE_API_BASE_URL;
        const { error } = useToastStore.getState();
        
        // First, check if we already have this negotiation in our store
        const existingNegotiation = get().negotiations.find(
          (n) => n.negotiation_id === negotiation_id
        );
        
        if (existingNegotiation) {
          return existingNegotiation;
        }
        
        // If not found in store, fetch from API
        set({ loading: true, error: null });
        showLoader(true);
        
        try {
          const response = await fetchWithHeaders(`${url}api/v1/negotiation-packages/${negotiation_id}`, {
            method: 'GET',
          });
          
          if (!response.ok) {
            if (response.status === 404) {
            error("Error", `Failed to fetch negotiation: ${response.statusText}`);
            }
            error("Error", `Failed to fetch negotiation: ${response.statusText}`);
          }
          
          const negotiation = await response.json();
          
          // Add the negotiation to our store if it's not already there
          const currentNegotiations = get().negotiations;
          if (!currentNegotiations.find((n) => n.negotiation_id === negotiation.negotiation_id)) {
            set((state) => ({
              negotiations: [...state.negotiations, negotiation],
              loading: false,
            }));
          } else {
            set({ loading: false });
          }
          
          return negotiation;
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : `Failed to fetch negotiation ${negotiation_id}`;
          set({ error: errorMessage, loading: false });
          console.error('Error fetching negotiation by ID:', error);
          return null;
        } finally {
          showLoader(false);
        }
      },

      createNegotiation: async (name: string, description?: string) => {
        const { showLoader } = useLoaderStore.getState();
        const url = import.meta.env.VITE_API_BASE_URL;
        const { error } = useToastStore.getState();
        
        set({ loading: true, error: null });
        showLoader(true);
        
        try {
          const payload: any = {
            index_id: 1, // hardcoded as requested
            name,
          };
          
          if (description) {
            payload.description = description;
          }
          
          const response = await fetchWithHeaders(`${url}api/v1/negotiation-packages/`, {
            method: 'POST',
            body: JSON.stringify(payload),
          });
          
          if (!response.ok) {
            error("Error", `Failed to create negotiation: ${response.statusText}`);
            throw new Error(`Failed to create negotiation: ${response.statusText}`);
          }
          
          const newNegotiation = await response.json();
          set((state) => ({
            negotiations: [...state.negotiations, newNegotiation],
            loading: false,
          }));
          
          return newNegotiation;
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to create negotiation';
          set({ error: errorMessage, loading: false });
          console.error('Error creating negotiation:', error);
          return null;
        } finally {
          showLoader(false);
        }
      },

      updateNegotiation: async (negotiation_id: number, data: Partial<Negotiation>) => {
        const { showLoader } = useLoaderStore.getState();
        const url = import.meta.env.VITE_API_BASE_URL;
        const { error } = useToastStore.getState();
        set({ loading: true, error: null });
        showLoader(true);
        
        try {
          const response = await fetchWithHeaders(`${url}api/v1/negotiation-packages/${negotiation_id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
          });
          
          if (!response.ok) {
            error("Error", `Failed to update negotiation: ${response.statusText}`);
          }
          
          const updatedNegotiation = await response.json();
          set((state) => ({
            negotiations: state.negotiations.map((n) =>
              n.negotiation_id === negotiation_id ? updatedNegotiation : n
            ),
            loading: false,
          }));
          
          return updatedNegotiation;
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to update negotiation';
          set({ error: errorMessage, loading: false });
          console.error('Error updating negotiation:', error);
          return null;
        } finally {
          showLoader(false);
        }
      },

      deleteNegotiation: async (negotiation_id: number) => {
        const { showLoader } = useLoaderStore.getState();
        const url = import.meta.env.VITE_API_BASE_URL;
        const { error } = useToastStore.getState();
        set({ loading: true, error: null });
        showLoader(true);
        
        try {
          const response = await fetchWithHeaders(`${url}api/v1/negotiation-packages/${negotiation_id}`, {
            method: 'DELETE',
          });
          
          if (!response.ok) {
            error("Error", `Failed to delete negotiation: ${response.statusText}`);
          }
          
          set((state) => ({
            negotiations: state.negotiations.filter((n) => n.negotiation_id !== negotiation_id),
            loading: false,
          }));
          
          return true;
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to delete negotiation';
          set({ error: errorMessage, loading: false });
          console.error('Error deleting negotiation:', error);
          return false;
        } finally {
          showLoader(false);
        }
      },


      fetchProductLists: async (negotiation_id: number) => {
        const { showLoader } = useLoaderStore.getState();
        const url = import.meta.env.VITE_API_BASE_URL;
        const { error } = useToastStore.getState();
        set({ productLists: [] });
        showLoader(true);
        
        try {
          let apiUrl = `${url}api/v1/tenders/products?negotiation_id=${negotiation_id}`;
          
          const response = await fetchWithHeaders(apiUrl, {
            method: 'GET',
          });
          
          if (!response.ok) {
            error("Error", `Failed to fetch tender bids`);
            set({ productLists: [] });
            return;
          }
          
          const data = await response.json();
          if (!Array.isArray(data?.products)) {
            set({ productLists: [] });
            return;
          }
          const updatedData = data.products.map((item: any) => {
            return {
             name : item.toUpperCase(),
             code: item
            };
          })
          
          set({ productLists: updatedData });
          return data?.products;
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to fetch product lists';
          set({ error: errorMessage });
          set({ productLists: [] });
        } finally {
          showLoader(false);
        }
      },

      clearProducts: () => {
        set({ productLists: [] })
        set({tenderSummary: []})
      },

      // Sourcing product operations (using tender_sourcing_product)
      fetchSourcingProducts: async (negotiation_id: number) => {
        const { showLoader } = useLoaderStore.getState();
        const url = import.meta.env.VITE_API_BASE_URL;
        const { error } = useToastStore.getState();
        set({ sourcingProducts: [] });
        showLoader(true);

        try {
          let apiUrl = `${url}api/v1/tenders/sourcing-products?negotiation_id=${negotiation_id}`;

          const response = await fetchWithHeaders(apiUrl, {
            method: 'GET',
          });

          if (!response.ok) {
            error("Error", `Failed to fetch products`);
            set({ sourcingProducts: [] });
            return;
          }

          const data = await response.json();
          if (!Array.isArray(data?.products)) {
            set({ sourcingProducts: [] });
            return;
          }

          set({ sourcingProducts: data.products });
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to fetch products';
          set({ error: errorMessage });
          set({ sourcingProducts: [] });
        } finally {
          showLoader(false);
        }
      },

      fetchCountryLists: async (negotiation_id: number, variant_id: string) => {
        const { showLoader } = useLoaderStore.getState();
        const url = import.meta.env.VITE_API_BASE_URL;
        const { error } = useToastStore.getState();
        set({ countryLists: [] });
        showLoader(true);

        try {
          let apiUrl = `${url}api/v1/tenders/sourcing-products/countries?negotiation_id=${negotiation_id}&variant_id=${encodeURIComponent(variant_id)}`;

          const response = await fetchWithHeaders(apiUrl, {
            method: 'GET',
          });

          if (!response.ok) {
            error("Error", `Failed to fetch countries`);
            set({ countryLists: [] });
            return;
          }

          const data = await response.json();
          if (!Array.isArray(data?.countries)) {
            set({ countryLists: [] });
            return;
          }

          set({ countryLists: data.countries });
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to fetch countries';
          set({ error: errorMessage });
          set({ countryLists: [] });
        } finally {
          showLoader(false);
        }
      },

      fetchProductBidSummary: async (negotiation_id: number, variant_id: string, sourcing_country?: string) => {
        const { showLoader } = useLoaderStore.getState();
        const url = import.meta.env.VITE_API_BASE_URL;
        const { error } = useToastStore.getState();
        set({ productBidSummaryLoading: true });
        showLoader(true);

        try {
          let apiUrl = `${url}api/v1/tenders/sourcing-products/bid-overview?negotiation_id=${negotiation_id}&variant_id=${encodeURIComponent(variant_id)}`;
          if (sourcing_country) {
            apiUrl += `&sourcing_country=${encodeURIComponent(sourcing_country)}`;
          }

          const response = await fetchWithHeaders(apiUrl, {
            method: 'GET',
          });

          if (!response.ok) {
            error("Error", `Failed to fetch bid summary`);
            set({ productBidSummary: null, productBidSummaryLoading: false });
            return;
          }

          const data = await response.json();
          set({ productBidSummary: data, productBidSummaryLoading: false });
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to fetch bid summary';
          set({ error: errorMessage, productBidSummary: null, productBidSummaryLoading: false });
        } finally {
          showLoader(false);
        }
      },

      clearSourcingProductData: () => {
        set({ sourcingProducts: [], countryLists: [], productBidSummary: null, productBidSummaryLoading: false });
      },

      fetchTenderSummary: async (negotiation_id: number, product_descriptions: string) => {
        const { showLoader } = useLoaderStore.getState();
        const url = import.meta.env.VITE_API_BASE_URL;
        const { error } = useToastStore.getState();
        set({ tenderSummary: [] });
        showLoader(true);
        
        try {
          let apiUrl = `${url}api/v1/tenders/products/insights?negotiation_id=${negotiation_id}&product_description=${product_descriptions}`;
          
          const response = await fetchWithHeaders(apiUrl, {
            method: 'GET',
          });
          
          if (!response.ok) {
            error("Error", `Failed to fetch tender summary data`);
            set({ tenderSummary: [] });
            return;
          }
          
          const data = await response.json();
          set({ tenderSummary: data });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to fetch tender summary data';
          set({ error: errorMessage });
          set({ tenderSummary: [] });
        } finally {
          showLoader(false);
        }
      },

      // Local state management
      setNegotiations: (negotiations) => set({ negotiations }),

      addNegotiation: (negotiation) =>
        set((state) => ({
          negotiations: [...state.negotiations, negotiation],
        })),

      removeNegotiation: (negotiation_id: number) =>
        set((state) => ({
          negotiations: state.negotiations.filter((n) => n.negotiation_id !== negotiation_id),
        })),

      setLoading: (loading) => set({ loading }),
      setError: (error) => set({ error }),

      // Form data management
      setBrand: (brand) =>
        set((s) => ({ data: { ...s.data, brand } })),

      setExtraGoals: (extra_goals) =>
        set((s) => ({ data: { ...s.data, extra_goals } })),

      setTargetSavings: (target_savings) =>
        set((s) => ({ data: { ...s.data, target_savings } })),

      setTargetSimulatedSavings: (target_simulated_savings) =>
        set((s) => ({ data: { ...s.data, target_simulated_savings } })),

      resetAllProductDescriptions: () => set({ allProductDescriptions: [] }), // Reset the allProductDescriptions

      resetInitialData: () => set({ data: initialData }),
      updateSupplierBid: async (
          negotiation_id: number,
          product_description: string,
          supplier_id: number | string,
          currentBid: number
        ) => {
          const { success, error } = useToastStore.getState();
          try {
            const url = import.meta.env.VITE_API_BASE_URL;
            let apiUrl = `${url}api/v1/tenders/products/update_supplier_bid?negotiation_id=${negotiation_id}&product_description=${product_description}&supplier_id=${supplier_id}&current_bid=${currentBid}`;
            const response = await fetchWithHeaders(apiUrl, {
              method: "PUT",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                negotiation_id: negotiation_id,
                product_description: product_description,
                supplier_id: supplier_id,
                current_bid: currentBid,
              }),
            });
            const data = await response.json().catch(() => null);
            if (!response.ok) {
              error("Error", data?.message || data?.detail || "Failed to update supplier bid");
              return null;
            }
            success("Success", data?.message || data?.status || "Supplier bid updated successfully.");
            return data;
          } catch (error) {
            console.error("Failed to update supplier bid", error);
            const message = error instanceof Error ? error.message : "Failed to update supplier bid";
            useToastStore.getState().error("Error", message);
            return null;
          }
      },
      updateConfig: async () => {
        const { showLoader } = useLoaderStore.getState();
        const url = import.meta.env.VITE_API_BASE_URL;
        const {negotiation_id} = useAuthStore.getState();
        const payload = {
          negotiation_id,
          config: {
            ...get().data,
          },
			};
        const { error } = useToastStore.getState();
        showLoader(true);
        
        try {
          const response = await fetchWithHeaders(`${url}api/v1/negotiation-packages/add_config`, {
            method: 'POST',
            body: JSON.stringify(payload)
          });
          const data = await response.json();
          
          if (!data?.success) {
            error("Error", `Failed to update config: ${data?.message}`);
            return null;
          };
          return data;
        } catch (error) {
          console.error('Error updating config:', error);
          return null;
        } finally {
          showLoader(false);
        }
      },
    }),
    { name: 'negotiations-store' }
  )
    // )
   
);
